import {
  Fragment,
  KeyboardEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Combobox } from '@headlessui/react';
import { IconPlus, IconZoomOutline } from '@iwoca/icons';
import { Input, Spinner } from '@iwoca/orion';
import { Trans } from '@lingui/macro';
import clsx from 'clsx';
import { ButtonLink } from 'components/ButtonLink/ButtonLink';
import styles from './SearchableDropdown.module.css';

type TProps<TValue> = {
  searchQuery?: string;
  placeholder?: string;
  onChange: (value: TValue) => void;
  optionsGetter: (searchQuery: string) => Promise<TValue[]>;
  optionRenderer: (value: TValue) => string;
  valueRenderer: (value: TValue) => string;
  onClear?: () => void;
  inputClassName?: string;
  labelComponent: ReactNode;
  disabled?: boolean;
};

export function SearchableDropdown<TValue>({
  onChange,
  searchQuery: initialSearchQuery,
  placeholder,
  optionsGetter,
  optionRenderer,
  valueRenderer,
  onClear,
  inputClassName,
  disabled = false,
  labelComponent,
}: TProps<TValue>) {
  // Each time the API is called, we add one to this value. This let's us continue to show
  // the spinner until the last call is resolved
  const [numberOfCallsLoading, setNumberOfCallsLoading] = useState(0);
  const [options, setOptions] = useState<TValue[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const keywordInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (keywordInputRef.current) {
      if (initialSearchQuery) {
        keywordInputRef.current.value = initialSearchQuery;
      }
      keywordInputRef.current.disabled = disabled;
    }
  }, [initialSearchQuery, disabled]);

  useEffect(() => {
    const optionSearch = setTimeout(async () => {
      if (searchQuery) {
        setNumberOfCallsLoading((prevNumber) => prevNumber + 1);
        try {
          const response = await optionsGetter(searchQuery);
          setOptions(response);
        } catch {
          setOptions([]);
        }
      } else {
        onClear?.();
        return setOptions([]);
      }

      setNumberOfCallsLoading((prevNumber) => prevNumber - 1);
    }, 200);

    return () => {
      clearTimeout(optionSearch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && numberOfCallsLoading > 0) {
      e.preventDefault();
    }
  };

  const handleOnClear = () => {
    setSearchQuery('');
    onClear?.();
    keywordInputRef.current!.value = '';
  };

  const renderOptions = useCallback(() => {
    if (numberOfCallsLoading > 0) {
      return (
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      );
    }

    return (
      <>
        {options.length > 0 ? (
          options.map((option, index) => (
            <Combobox.Option
              className={({ active }) =>
                clsx(styles.option, { [styles.active]: active })
              }
              key={index}
              value={option}
            >
              {optionRenderer(option)}
            </Combobox.Option>
          ))
        ) : (
          <p className={styles.noResults}>
            <Trans>No results</Trans>
          </p>
        )}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberOfCallsLoading, options]);

  return (
    <Combobox onChange={onChange}>
      <div className={styles.companyInputLabel}>{labelComponent}</div>
      <div className={styles.companiesSearch}>
        <Combobox.Input
          onChange={(event) => {
            setSearchQuery(event.target.value);
          }}
          as={Fragment}
          displayValue={(inputValue: TValue) => {
            if (inputValue && searchQuery) {
              return valueRenderer(inputValue);
            }
            return '';
          }}
        >
          <Input
            iconRight={!searchQuery ? <IconZoomOutline /> : undefined}
            placeholder={placeholder}
            className={clsx(styles.companyInput, inputClassName)}
            onKeyDown={handleKeyDown}
            ref={keywordInputRef}
          />
        </Combobox.Input>
        {searchQuery && (
          <ButtonLink
            data-testid="clear-button"
            onClick={handleOnClear}
            type="button"
            className={styles.clearButton}
          >
            <IconPlus height="24" width="24" />
          </ButtonLink>
        )}
        {searchQuery && (
          <Combobox.Options className={clsx(styles.options, 'fs-mask')}>
            {renderOptions()}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
}
